/* eslint-disable indent */
import {
  createAsyncThunk,
  createEntityAdapter,
  createSlice,
  combineReducers,
  PayloadAction,
  createSelector,
  createAction,
} from "@reduxjs/toolkit";
import axios from "axios";
import { Project } from "../util/dataTypes/Project";
import { FlatGraphResponse } from "../util/dataTypes/FlatGraphResponse";
import { Action } from "../util/dataTypes/Action";
import { Content } from "../util/dataTypes/Content";
import {
  ContentType,
  ContentTypeAsset,
  ContentTypeScene,
  ModelName,
  ValidContentType,
} from "../util/dataTypes/ContentType";
import { buildGraph } from "../util/graphBuilder";
import { normalizeContentTypeObject } from "../util/schemas";
import apiUrls from "../../../api";
import { IMedia } from "../../../model/unityObject";
import { RootState } from "../../../common/state/store";
import { IAppLoaderAction } from "../../../common/state/loaderHandleMiddleware";
import { ILanguage, IProjectLanguageData } from "src/model/model";
import { stripEmptyMediaRelations } from "../util/mediaRelationKeys";

// export let throwError:any = null;
// export const setThrowError = (th:any) => throwError = th;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const throwError = (_error: any, _userMessage = "") => {
  //@todo
  // const a = getThrowErrorFuncEditProjectScenes(store.);
  /*const func: any = dirtyGetThrowErrorFuncEditProjectScenes();
  func(error);*/
};

export const loadGraph = createAsyncThunk("data/loadGraph", (projectId: number) => {
  try {
    return Promise.resolve(
      axios.get<FlatGraphResponse>(`${apiUrls.projects.graph.flat}/${projectId}?isForGraph=true`),
    );
  } catch (error) {
    throwError(error);
    return Promise.reject(error);
  }
});

export const checkValidityOnly = createAsyncThunk(
  "data/checkValidityOnly",
  async (projectId: number) => {
    try {
      const response = await axios.get(
        `${apiUrls.projects.graph.flat}/${projectId}?checkValidityOnly=true&isForGraph=true`,
      );

      // Extract only the necessary serializable data
      const { data } = response;
      return { data };
    } catch (error) {
      throwError(error);
      return Promise.reject(error);
    }
  },
);

export const copySceneToProject = createAsyncThunk(
  "graph/copySceneToProject",
  async ({
    sourceContentSceneId,
    sourceProjectId,
    targetProjectId,
    replaceContentSceneId,
  }: {
    sourceContentSceneId: number;
    sourceProjectId: number;
    targetProjectId: number;
    replaceContentSceneId?: number;
  } & IAppLoaderAction) => {
    const response = await axios.post(apiUrls.projects.copySceneToProject, {
      sourceContentSceneId,
      sourceProjectId,
      targetProjectId,
      replaceContentSceneId,
    });
    return { data: response.data };
  },
);

export const fetchUnlinkedScenes = createAsyncThunk(
  "graph/fetchUnlinkedScenes",
  async ({ projectId }: { projectId: number } & IAppLoaderAction) => {
    const response = await axios.get(`${apiUrls.projects.unlinkedScenes}/${projectId}`);
    return { data: response.data };
  },
);

export type UpdateContentTypeParams = {
  contentType: ContentTypeScene | ContentTypeAsset;
  updatedProjectLanguageDatas: Array<{ id: number } & any>;
  changes: any;
  activeLanguageId: number;
  sourceLanguageId: number;
  callback?: any;
  allMedias?: IMedia[];
  shouldDirtyRefreshOnCompletion?: boolean;
};

export const updateContentType = createAsyncThunk(
  "data/updateContentType",
  async ({
    contentType,
    updatedProjectLanguageDatas,
    changes,
    activeLanguageId,
    sourceLanguageId,
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    callback = () => {},
    shouldDirtyRefreshOnCompletion = false,
  }: UpdateContentTypeParams & IAppLoaderAction) => {
    try {
      const isEditingSourceLanguage = Number(activeLanguageId) === Number(sourceLanguageId);
      // here we handle the fact that the 'name' displayed actually belongs to the content (thats also the case in VR)
      const contentId = contentType?.object?.content;
      if (isEditingSourceLanguage && contentId && Object.keys(changes).includes("name")) {
        const relevantObjectCurrentName = contentType?.object.name;
        const relevantIncomingName = changes["name"];
        if (relevantObjectCurrentName !== relevantIncomingName) {
          await axios
            .put(`${apiUrls.contents}/${contentId}`, { name: relevantIncomingName })
            .then((res) => res);
        }
      }
      const path = deduceEndpointFromContentType(contentType);
      const pldPromises = updatedProjectLanguageDatas.map((pld) => {
        const pldPayload = { ...pld };
        delete pldPayload.id;
        return axios.put(`${apiUrls.projects.projectLanguageDatas}/${pld.id}`, {
          data: pldPayload,
        });
      });
      const promiseToUpdateContentType = () => axios.put(path, stripEmptyMediaRelations(changes));

      const promises: Promise<any>[] = [...pldPromises];
      if (isEditingSourceLanguage) {
        promises.push(promiseToUpdateContentType());
      }
      const responses = await Promise.all(promises);
      const contentTypeResponse = isEditingSourceLanguage ? responses[responses.length - 1] : null;
      callback();

      if (shouldDirtyRefreshOnCompletion) {
        const dirtyLoc = window.location.href;
        //eslint-disable-next-line
        window.location.href = dirtyLoc;
      }
      if (contentTypeResponse) {
        return normalizeContentTypeObject(contentTypeResponse.data);
      }
      return null;
    } catch (error) {
      console.error("Error in updateCotentType : ", error);
      throwError(error);
      // Since this is a thunk, and we might not want to throw errors to the Redux middleware,
      // we can return a rejected promise instead to ensure all paths return a value.
      return Promise.reject(error);
    }
  },
);

const deduceEndpointFromContentType = (contentType: ContentTypeScene | ContentTypeAsset) => {
  const { modelName } = contentType;
  const { id } = contentType.object;
  return deduceEndpointFromModelName(modelName, id);
};

const deduceEndpointFromModelName = (modelName: ModelName, id?: number) => {
  // the endpoints are named in a systematic manner, and the rules can be inferred from simple examples
  // - by default, adding a final "s" to the name:
  //   "content-audio" ➡︎ /content-audios
  //   "content-clean" ➡︎ /content-cleans
  // - but beware: when the modelName ends with a number, we add a "-" inbetween
  //   "content-quizz-2" ➡︎ /content-quizz-2-s
  //   "content-button-1" ➡︎ /content-button-1-s
  // - also not that this rule (strangely ?) applies when the number is not at the end:
  //   "content-video-2d" ➡︎ /content-video-2-ds

  // step 1: handle the numbers, if any
  let path = modelName;

  const numberMatch = path.match(/(\d+)([^\d]*)$/);
  if (numberMatch) {
    // If there's a number at the end, add '-' before 's', adjusting for additional non-numeric characters if present
    // ... Well, unless content video 2d, of course 🙈
    if (modelName !== "content-video-2-d") {
      path += numberMatch[2] ? `-${numberMatch[2]}` : "-";
    }
  }
  // ... Sometimes you just have to pile "ifs" to dance around strapi endpoint naming
  if (path === "content-photo-catch") {
    path += "e";
  }
  // "quizz" pluralises to "quizzes", not "quizzs": insert an "e" before the
  // trailing "s" (same trick as photo-catch). Only content-random-quizz ends
  // in a bare "quizz" — the others end in a number (content-quizz-3 ->
  // content-quizz-3-s) or a word (content-quizz-sort -> -sorts).
  if (path === "content-random-quizz") {
    path += "e";
  }
  if (modelName === "content-3d-viewer" || modelName === "content-3-d-viewer") {
    path = "content-3-d-viewer" as "content-3d-viewer";
  }

  // step 2: add the "s"
  path += "s";
  // step 3: when given an id, we suffix
  if (id !== undefined) {
    path += `/${id}`;
  }

  // step 4: finally we prefix with the base URL
  return `${apiUrls.base}/api/${path}`;
};

export const resetGraphInfo = createAction("data/resetGraph");

//
// PROJECTS
//

const graphProjectSlice = createSlice({
  name: "graph/project",
  initialState: null as Project | null,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(loadGraph.fulfilled, (_state, action) => {
      return action?.payload?.data.project;
    });
    builder.addCase(resetGraphInfo, () => null);
  },
});

//
// ACTIONS
//

const actionsAdapter = createEntityAdapter<Action>();

const graphActionsSlice = createSlice({
  name: "graph/actions",
  initialState: actionsAdapter.getInitialState(),
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(loadGraph.fulfilled, (state, action) => {
      try {
        actionsAdapter.setAll(state, action?.payload?.data?.actions ?? {});
      } catch (error) {
        throwError(error);
      }
    });
    builder.addCase(resetGraphInfo, (state) => {
      actionsAdapter.removeAll(state);
    });
  },
});

export const { selectAll: selectAllGraphActions } = actionsAdapter.getSelectors(
  (state: RootState) => state.graphLegacy.actions,
);

//
// SOURCE-MEDIAS
//

const sourceMediasAdapter = createEntityAdapter<IMedia>();

const graphSourceMedias = createSlice({
  name: "graph/sourceMedias",
  initialState: sourceMediasAdapter.getInitialState(),
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(loadGraph.fulfilled, (state, action) => {
      try {
        sourceMediasAdapter.setAll(state, action?.payload?.data?.projectSourceMedias ?? {});
      } catch (error) {
        throwError(error);
      }
    });
    builder.addCase(resetGraphInfo, (state) => {
      sourceMediasAdapter.removeAll(state);
    });
  },
});

//
// CONTENTS
//
const contentsAdapter = createEntityAdapter<Content>();

const graphContentsSlice = createSlice({
  name: "graph/contents",
  initialState: contentsAdapter.getInitialState(),
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(loadGraph.fulfilled, (state, action) => {
      contentsAdapter.setAll(state, action?.payload?.data?.contents ?? {});
    });
    builder.addCase(resetGraphInfo, (state) => {
      contentsAdapter.removeAll(state);
    });
  },
});

export const { selectAll: selectAllGraphContents, selectEntities: selectGraphContentByIds } =
  contentsAdapter.getSelectors((state: RootState) => state.graphLegacy.contents);

//
// PROJECT LANGUAGE DATAS
//

export const fetchMapContentKeysWithPldKeys = createAsyncThunk(
  "data/getMapContentKeysWithPldKeys",
  async () => {
    const response = await axios.get(
      `${apiUrls.projects.projectLanguageDatas}/mapContentKeysWithPldKeys`,
    );
    return response.data;
  },
);

// Fetches all PLDs for a single content via the standard Strapi endpoint and
// flattens the v4 wrapper shape so the result matches what loadGraph stores.
// Used to refresh translation data without reloading the whole graph.
export const refetchPldsForContent = createAsyncThunk(
  "data/refetchPldsForContent",
  async (contentId: number) => {
    const response = await axios.get(
      `${apiUrls.projects.projectLanguageDatas}?filters[content][$eq]=${contentId}&pagination[pageSize]=100`,
    );
    const raw: any[] = response.data?.data ?? [];
    return raw.map((row: any) => {
      const attrs = row?.attributes ?? row;
      const flat: any = { id: row.id, ...attrs };
      if (attrs?.content?.data?.id != null) flat.content = attrs.content.data.id;
      if (attrs?.language?.data?.id != null) flat.language = attrs.language.data.id;
      return flat as IProjectLanguageData;
    });
  },
);

const projectLanguageDatasAdapter = createEntityAdapter<IProjectLanguageData>();

const initialState = projectLanguageDatasAdapter.getInitialState({
  currentLanguagePldsMappedByContent: {},
  mapContentKeysWithPldKeys: {},
  relevantPldAfterUpdate: {},
});

export const getMapContentKeysWithPldKeys = (state: RootState) =>
  state.graphLegacy.projectLanguageDatas.mapContentKeysWithPldKeys;

const graphProjectLanguageDatasSlice = createSlice({
  name: "graph/projectLanguageDatas",
  initialState,
  reducers: {
    resetProjectLanguageDatas: (state) => {
      projectLanguageDatasAdapter.removeAll(state);
      state.currentLanguagePldsMappedByContent = {};
    },
    updateRelevantPld: (state, action: PayloadAction<{ contentId: string; updatedPld: any }>) => {
      const { contentId, updatedPld } = action.payload;
      state.currentLanguagePldsMappedByContent[contentId] = {
        ...state.currentLanguagePldsMappedByContent[contentId],
        projectLanguageData: updatedPld,
      };
    },

    setRelevantPldAfterUpdate: (state, action: PayloadAction<IProjectLanguageData>) => {
      state.relevantPldAfterUpdate = action.payload;
    },

    // Optimistic patch for PLD entities. Mirrors what loadGraph used to do for
    // single nodes, without re-fetching the entire graph.
    patchPlds: (
      state,
      action: PayloadAction<Array<{ id: number; changes: Partial<IProjectLanguageData> }>>,
    ) => {
      projectLanguageDatasAdapter.updateMany(state, action.payload);
      action.payload.forEach(({ id, changes }) => {
        const updated = state.entities[id];
        if (!updated) return;
        const contentId = (updated as any).content;
        const existing = state.currentLanguagePldsMappedByContent[contentId];
        if (existing && Number(existing.projectLanguageData?.id) === Number(id)) {
          state.currentLanguagePldsMappedByContent[contentId] = {
            ...existing,
            projectLanguageData: { ...existing.projectLanguageData, ...changes },
          };
        }
      });
    },
  },
  extraReducers: (builder) => {
    builder.addCase(loadGraph.fulfilled, (state, action) => {
      projectLanguageDatasAdapter.setAll(state, action.payload?.data?.projectLanguageDatas);

      // Assuming project and contents are also loaded here
      const project = action.payload?.data.project;
      const contents = action.payload?.data.contents.reduce((acc, content) => {
        acc[content.id] = content;
        return acc;
      }, {});

      // Reset currentLanguagePldsMappedByContent before populating
      state.currentLanguagePldsMappedByContent = {};

      Object.values(state.entities).forEach((pld) => {
        const projectLanguageId =
          typeof project.language === "object" && project.language !== null
            ? (project.language as ILanguage).id
            : project.language;
        if (project && pld.language === projectLanguageId) {
          state.currentLanguagePldsMappedByContent[pld.content] = {
            languageId: projectLanguageId,
            content: contents[pld.content],
            projectLanguageData: pld,
          };
        }
      });
    });

    builder.addCase(fetchMapContentKeysWithPldKeys.fulfilled, (state, action) => {
      state.mapContentKeysWithPldKeys = action.payload;
    });

    // Mirror the PLD writes the updateContentType thunk just made to the server,
    // so the slice stays in sync without re-fetching the whole graph.
    builder.addCase(updateContentType.fulfilled, (state, action) => {
      const updatedPlds = action.meta?.arg?.updatedProjectLanguageDatas ?? [];
      const updates = updatedPlds
        .filter((pld: any) => pld?.id != null)
        .map((pld: any) => {
          const { id, ...changes } = pld;
          return { id, changes };
        });
      if (updates.length === 0) return;
      projectLanguageDatasAdapter.updateMany(state, updates);
      updates.forEach(({ id, changes }) => {
        const updated = state.entities[id];
        if (!updated) return;
        const contentId = (updated as any).content;
        const existing = state.currentLanguagePldsMappedByContent[contentId];
        if (existing && Number(existing.projectLanguageData?.id) === Number(id)) {
          state.currentLanguagePldsMappedByContent[contentId] = {
            ...existing,
            projectLanguageData: { ...existing.projectLanguageData, ...changes },
          };
        }
      });
    });

    builder.addCase(refetchPldsForContent.fulfilled, (state, action) => {
      projectLanguageDatasAdapter.upsertMany(state, action.payload);
      action.payload.forEach((pld: any) => {
        const contentId = pld.content;
        const existing = state.currentLanguagePldsMappedByContent[contentId];
        if (existing && Number(existing.projectLanguageData?.id) === Number(pld.id)) {
          state.currentLanguagePldsMappedByContent[contentId] = {
            ...existing,
            projectLanguageData: pld,
          };
        }
      });
    });

    builder.addCase(resetGraphInfo, (state) => {
      projectLanguageDatasAdapter.removeAll(state);
      state.currentLanguagePldsMappedByContent = {};
      // We want to persist the mapContentKeysWithPldKeys
      const preservedMapContentKeysWithPldKeys = state.mapContentKeysWithPldKeys;
      state.mapContentKeysWithPldKeys = preservedMapContentKeysWithPldKeys;
    });
  },
});

export const { updateRelevantPld, patchPlds } = graphProjectLanguageDatasSlice.actions;

//
//  CONTENT TYPES
//
const makeContentTypeUniqueKey = (contentType: ContentType) => {
  try {
    const x = `${contentType.modelName}-${contentType.object.id}`;
    return x;
  } catch (error) {
    throwError(error);
    return null;
  }
};

const contentTypesAdapter = createEntityAdapter<ContentType>({
  selectId: makeContentTypeUniqueKey as any,
});

const graphContentTypesSlice = createSlice({
  name: "graph/contentTypes",
  initialState: contentTypesAdapter.getInitialState(),
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(loadGraph.fulfilled, (state, action) => {
      try {
        contentTypesAdapter.setAll(state, action?.payload?.data?.contentTypes ?? {});
        // here: create a new state.graphLegacy.projectLanguageData to store them.
      } catch (error) {
        throwError(error);
      }
    });
    builder.addCase(updateContentType.fulfilled, (state, action) => {
      try {
        if (!action.payload) return;
        // ⚠️ the endpoint returns the content type OBJECT !
        // we need to "rebuild" a content-type from the result and the modelName of the args
        const contentTypeObject =
          action?.payload?.entities.contentTypeObjects[action.payload.result];
        const { modelName } = action.meta.arg.contentType;
        const contentType = { modelName, object: contentTypeObject } as ValidContentType;

        // then we can update the local valu
        contentTypesAdapter.updateOne(state, {
          id: makeContentTypeUniqueKey(contentType) as any,
          changes: contentType,
        });
      } catch (error) {
        throwError(error);
      }
    });
    builder.addCase(updateContentType.rejected, (_state, action) => {
      try {
        return action.meta.arg.callback && typeof action.meta.arg.callback === "function"
          ? action.meta.arg.callback()
          : null;
      } catch (error) {
        throwError(error);
      }
    });
    builder.addCase(resetGraphInfo, (state) => {
      try {
        contentTypesAdapter.removeAll(state);
      } catch (error) {
        throwError(error);
      }
    });
  },
});

export const { selectAll: selectAllGraphContentTypes } = contentTypesAdapter.getSelectors(
  (state: RootState) => state.graphLegacy.contentTypes,
);

//
// GLOBAL "DATA-LEVEL" STATE
//

const selectedContentSlice = createSlice({
  name: "graph/selectedContent",
  initialState: null as number | null,
  reducers: {
    /// defines a project as "the active project"
    setSelectedContent: (_state: any, action: PayloadAction<number | null>) => action.payload,
  },
  extraReducers: (builder) => {
    builder.addCase(resetGraphInfo, () => null);
  },
});

export const selectSelectedContentId = (state: RootState) => state.graphLegacy.selectedContent;
export const projectContents = (state: RootState) => state.graphLegacy.contents;

const graphErrorMessagesSlice = createSlice({
  name: "graph/errorMessages",
  initialState: [] as { projectId: number; messages: string[] }[],
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(loadGraph.fulfilled, (state, action) => {
      const projectId = action.payload.data.project.id;
      const newErrorMessages = action.payload.data.errorMessages || [];
      const existingEntry = state.find((entry) => entry.projectId === projectId);
      if (existingEntry) {
        existingEntry.messages = newErrorMessages;
      } else {
        state.push({ projectId, messages: newErrorMessages });
      }
    });
    builder.addCase(checkValidityOnly.fulfilled, (state, action) => {
      const projectId = action.payload.data.project.id;
      const newErrorMessages = action.payload.data.errorMessages || [];
      const existingEntry = state.find((entry) => entry.projectId === projectId);
      if (existingEntry) {
        existingEntry.messages = newErrorMessages;
      } else {
        state.push({ projectId, messages: newErrorMessages });
      }
    });
    builder.addCase(resetGraphInfo, () => []);
  },
});

const loading = (state = false, action: any) => {
  if (loadGraph.pending.match(action) || updateContentType.pending.match(action)) {
    return false;
  } else if (loadGraph.rejected.match(action) || loadGraph.fulfilled.match(action)) {
    return false;
  } else if (
    updateContentType.rejected.match(action) ||
    updateContentType.fulfilled.match(action)
  ) {
    return false;
  } else if (resetGraphInfo.match(action)) {
    return false;
  }
  return state;
};

// Aurelien's error handling (see https://redux-toolkit.js.org/api/createAsyncThunk#type )
const error = (state: Error | null = null, action: any) => {
  if (loadGraph.pending.match(action) || loadGraph.fulfilled.match(action)) {
    return null;
  } else if (loadGraph.rejected.match(action)) {
    throwError(action.error);
    return action.error;
  } else if (updateContentType.pending.match(action) || updateContentType.fulfilled.match(action)) {
    return null;
  } else if (updateContentType.rejected.match(action)) {
    throwError(action.error);
    return action.error;
  } else if (resetGraphInfo.match(action)) {
    return null;
  }
  return state;
};

// FINAL DATA REDUCER

const graph = combineReducers({
  loading,
  error,
  selectedContent: selectedContentSlice.reducer,
  project: graphProjectSlice.reducer,
  actions: graphActionsSlice.reducer,
  contents: graphContentsSlice.reducer,
  contentTypes: graphContentTypesSlice.reducer,
  sourceMedias: graphSourceMedias.reducer,
  projectLanguageDatas: graphProjectLanguageDatasSlice.reducer,
  errorMessages: graphErrorMessagesSlice.reducer,
});

export type Graph = ReturnType<typeof graph>;

// SELECTORS

// Import showAllAssets from graphuSlice
import { getShowAllAssets } from "./graphuSlice";

export const getGraphLayout = createSelector(
  [selectAllGraphContents, selectAllGraphContentTypes, selectAllGraphActions, getShowAllAssets],
  (contents, contentTypes, actions, showAllAssets) =>
    buildGraph(contents, contentTypes, actions, { showAllAssets }),
);

export const getNodeById = (state: RootState, nodeId: string, modelName?: string) => {
  const allContentTypes = selectAllGraphContentTypes(state);
  return allContentTypes.find(
    (node) =>
      Number(node.object.id) === Number(nodeId) && (node.modelName === modelName || !modelName),
  );
};

export const relevantPldForContent = (contentId) =>
  createSelector(
    (state: RootState) => state.graphLegacy.projectLanguageDatas.currentLanguagePldsMappedByContent,
    (pldsMappedByContent) => {
      const pld = pldsMappedByContent[contentId];
      if (!pld) {
        console.error(
          `No project language data found for content ID ${contentId}; pldsMappedByContent:`,
          pldsMappedByContent,
        );
        return undefined;
      }
      return pld;
    },
  );

export const relevantPldsForContent = (contentId) =>
  createSelector(
    (state: RootState) => state.graphLegacy.projectLanguageDatas.entities,
    (pldEntities) => {
      const plds = Object.values(pldEntities).filter(
        (pld) => Number(pld.content) === Number(contentId),
      );

      if (plds.length === 0) {
        console.error(`No project language data found for content ID ${contentId}`);
        return undefined;
      }
      return plds;
    },
  );

export default graph;
